import React from "react";
import { useNavigate } from "react-router-dom";
import { FaUndoAlt, FaArrowLeft } from "react-icons/fa";
import "../../../assets/css/customer/returnPolicy/ReturnPolicy.css";

const SECTIONS = [
  {
    title: "1. Return Eligibility",
    body: (
      <>
        <p>Customers may request a return only under the following circumstances:</p>
        <ul className="rp-list">
          <li>The product received is damaged upon delivery.</li>
          <li>The wrong product has been delivered.</li>
          <li>The product contains a verified manufacturing defect.</li>
        </ul>
        <p>All return requests are subject to review and approval by AD Furniture House.</p>
      </>
    ),
  },
  {
    title: "2. Return Request Period",
    body: (
      <>
        <p>
          Customers must submit a return request within <strong>7 days</strong> of
          receiving their order.
        </p>
        <p>Return requests submitted after the specified period may not be accepted.</p>
      </>
    ),
  },
  {
    title: "3. Product Condition",
    body: (
      <>
        <p>To be eligible for a return, the product should:</p>
        <ul className="rp-list">
          <li>Be in its original condition.</li>
          <li>Not show signs of misuse, negligence, or intentional damage.</li>
          <li>
            Include all original accessories, packaging, and documentation
            whenever possible.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "4. Non-Returnable Products",
    body: (
      <>
        <p>The following products are not eligible for return:</p>
        <ul className="rp-list">
          <li>Custom-made furniture manufactured according to customer requirements.</li>
          <li>Furniture damaged due to improper handling or misuse by the customer.</li>
          <li>Products that have been altered or modified after delivery.</li>
          <li>
            Clearance, promotional, or final-sale items unless they contain a
            verified manufacturing defect.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "5. Custom Furniture Orders",
    body: (
      <>
        <p>Furniture manufactured according to the customer's selected:</p>
        <ul className="rp-list">
          <li>Design</li>
          <li>Measurements</li>
          <li>Material</li>
          <li>Color</li>
          <li>Other customization requirements</li>
        </ul>
        <p>
          cannot be returned unless the delivered product contains a
          verified manufacturing defect or is different from the confirmed
          order.
        </p>
      </>
    ),
  },
  {
    title: "6. Damaged or Incorrect Products",
    body: (
      <>
        <p>
          If you receive a damaged, defective, or incorrect product, please
          contact our support team as soon as possible and provide:
        </p>
        <ul className="rp-list">
          <li>Your Order ID</li>
          <li>Clear photographs of the product</li>
          <li>A brief description of the issue</li>
        </ul>
        <p>
          Our team will review your request and provide an appropriate
          solution according to our return policy.
        </p>
      </>
    ),
  },
  {
    title: "7. Order Cancellation",
    body: (
      <>
        <p>
          Customers may request order cancellation before the order has
          been shipped or before production of custom furniture has
          started.
        </p>
        <p>
          Once an order has been shipped or custom furniture production has
          begun, cancellation requests may not be accepted.
        </p>
      </>
    ),
  },
  {
    title: "8. Shipping Charges",
    body: (
      <p>
        Customers are responsible for all shipping charges related to
        product delivery and returns. Any shipping costs incurred during the
        return process must be borne by the customer.
      </p>
    ),
  },
  {
    title: "9. AI Room Design Feature",
    body: (
      <>
        <p>
          The AI Room Design feature is provided to help customers
          visualize furniture placement within their rooms.
        </p>
        <p>
          The generated AI designs are intended for visualization purposes
          only. Return requests cannot be made solely because the
          AI-generated design differs from the customer's expectations or
          the final appearance after product placement.
        </p>
      </>
    ),
  },
  {
    title: "10. Contact Us",
    body: (
      <p>
        If you have any questions regarding this Return Policy or wish to
        request a return, please contact us through the{" "}
        <a href="/contact-us" className="rp-inline-link">
          Contact Us
        </a>{" "}
        page available on our website. Our support team will review your
        request and respond as soon as possible.
      </p>
    ),
  },
];

const ReturnPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="rp-page">
      {/* ── Back Button ── */}
      <button className="rp-back-btn" onClick={() => navigate(-1)}>
        <FaArrowLeft /> Back
      </button>

      {/* ── Header ── */}
      <div className="rp-header">
        <div className="rp-header-icon">
          <FaUndoAlt />
        </div>
        <h1 className="rp-header-title">Return Policy</h1>
        <p className="rp-header-subtitle">
          Please read this Return Policy carefully before placing an order
          through AD Furniture House.
        </p>
      </div>

      {/* ── Content ── */}
      <div className="rp-content">
        <p className="rp-intro">
          Welcome to <strong>AD Furniture House</strong>. We are committed to
          delivering high-quality furniture and ensuring customer
          satisfaction. Please read this Return Policy carefully before
          placing an order through our website. By purchasing from AD
          Furniture House, you agree to the following return terms and
          conditions.
        </p>

        {SECTIONS.map(({ title, body }, index) => (
          <section className="rp-section" key={index}>
            <h2 className="rp-section-title">{title}</h2>
            <div className="rp-section-body">{body}</div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ReturnPolicy;